/**
 * Loading and refresh for one patient's eligibility detail payload.
 */

import { useCallback, useEffect, useState } from "react";
import { clinicalTrialsService } from "../api/clinicalTrialsService";
import type { EligibilityDetailResponse } from "../data-model/eligibilityDetail";
import { API_ENDPOINTS } from "../enums/apiUrls";
import { toUserFacingApiErrorMessage } from "../utils/apiErrorMessage";

/**
 * Abort a stalled detail request rather than leaving the page spinning.
 */
const REQUEST_TIMEOUT_MS = 20000;

/** State returned by {@link useEligibilityDetail}. */
export interface EligibilityDetailState {
  detail: EligibilityDetailResponse | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

/**
 * Fetch `GET /eligibility/{trialId}/{mrn}/detail` once on mount.
 *
 * This is the only first-paint call for the details screen. Overrides already
 * arrive on `detail.overrides`.
 *
 * @param trialId - Trial the patient is evaluated against
 * @param mrn - Patient medical record number
 * @return EligibilityDetailState: Detail payload plus loading, error, and refresh
 */
export function useEligibilityDetail(
  trialId: string,
  mrn: string,
): EligibilityDetailState {
  const [detail, setDetail] = useState<EligibilityDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  /**
   * Re-request eligibility detail from the service.
   *
   * @return void
   */
  const refresh = useCallback((): void => {
    setReloadToken((current) => current + 1);
  }, []);

  useEffect(() => {
    if (!trialId || !mrn) {
      setDetail(null);
      setLoading(false);
      setError("Missing trial or MRN");
      return undefined;
    }
    const controller = new AbortController();
    let timedOut = false;
    const timer = window.setTimeout(() => {
      timedOut = true;
      controller.abort();
    }, REQUEST_TIMEOUT_MS);
    setLoading(true);
    setError(null);
    const url = API_ENDPOINTS.ELIGIBILITY_DETAIL(trialId, mrn);
    clinicalTrialsService
      .getEligibilityDetail(trialId, mrn, controller.signal)
      .then((response) => {
       
        setDetail(response);
      })
      .catch((reason: unknown) => {
        if (controller.signal.aborted && !timedOut) return;
        console.error("[eligibility-detail] failed", url, reason);
        if (timedOut) {
          setError("Eligibility detail request timed out");
          return;
        }
        setError(
          toUserFacingApiErrorMessage(
            reason,
            "Failed to load eligibility detail",
          ),
        );
      })
      .finally(() => {
        window.clearTimeout(timer);
        if (timedOut || !controller.signal.aborted) setLoading(false);
      });
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [mrn, reloadToken, trialId]);

  return { detail, loading, error, refresh };
}
